import { Cookie } from 'lucide-react';
import type { VisitorConfig } from '../../types';

// Bandeau cookies, présent sur toutes les pages du site public jusqu'à ce que
// le visiteur ait répondu — mémorisé dans son navigateur, pas auprès de
// l'agence. Visibilité pilotée par le parent (SiteWidgets.tsx), qui doit aussi
// décaler le bouton de chat flottant pendant que ce bandeau occupe le bas de
// l'écran.
//
// Deux modes :
// - Sans balise publicitaire (cas par défaut) : un seul bouton d'acquittement.
//   Le site ne dépose alors que des cookies techniques, il n'y a rien à
//   consentir au sens du RGPD, seulement à porter à la connaissance du
//   visiteur. Proposer un choix sur des cookies qui n'existent pas serait
//   trompeur.
// - Avec une balise Google (config.googleTagId) : vrai choix Accepter / Refuser,
//   à poids égal — refuser doit être aussi simple qu'accepter.

interface CookieConsentBannerProps {
  config: VisitorConfig;
  onDismiss: () => void;
  onAccept?: () => void;
  onRefuse?: () => void;
}

export const TRACKING_BANNER_TEXT =
  "Nous utilisons des cookies de mesure publicitaire (Google Ads) pour évaluer l'efficacité de nos campagnes. Ils ne sont déposés qu'avec votre accord.";

export function CookieConsentBanner({ config, onDismiss, onAccept, onRefuse }: CookieConsentBannerProps) {
  const tracking = Boolean(config.googleTagId?.trim());
  const text = tracking ? TRACKING_BANNER_TEXT : config.cookieBannerText?.trim() || '';
  if (!text) return null;

  const policyUrl = config.footerLegalLinks?.rgpdUrl || '/politique-cookies';

  return (
    <div className="fixed bottom-0 inset-x-0 z-40 bg-slate-950 text-white/80 px-4 py-3.5">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center gap-3 text-xs">
        <Cookie className="w-5 h-5 text-white/50 shrink-0 hidden sm:block" />
        <p className="flex-1 leading-relaxed">
          {text}{' '}
          <a href={policyUrl} className="underline hover:text-white">
            En savoir plus
          </a>
        </p>
        {tracking ? (
          <div className="flex gap-2 shrink-0">
            <button
              onClick={onRefuse}
              className="px-4 py-1.5 bg-white text-slate-900 rounded-lg text-xs font-bold hover:bg-slate-100 cursor-pointer"
            >
              Refuser
            </button>
            <button
              onClick={onAccept}
              className="px-4 py-1.5 bg-white text-slate-900 rounded-lg text-xs font-bold hover:bg-slate-100 cursor-pointer"
            >
              Accepter
            </button>
          </div>
        ) : (
          <button
            onClick={onDismiss}
            className="shrink-0 px-4 py-1.5 bg-white text-slate-900 rounded-lg text-xs font-bold hover:bg-slate-100 cursor-pointer"
          >
            J'ai compris
          </button>
        )}
      </div>
    </div>
  );
}
